import React, { useMemo, useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { useT } from '../i18n/useT';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { F, MONO_FEAT, mr, DUR } from '../styles/theme';
import { COIN_LIST, fetchMarketData } from '../utils/coingecko';
import { fmt } from '../utils/format';
import CoinImage from '../components/CoinImage';
import { Icon, Icons } from '../components/red/Icons';
import { useFavorites } from '../hooks/useFavorites';
import MarketIndices from '../components/red/MarketIndices';
import { TopMoverStrip } from './Dashboard';
import { usePillMotion, Segmented } from '../components/red/Editorial';
import { usePress } from '../components/red/Press';

// Quelle: design_handoff_red_webapp/source/red-screens-markets.jsx
// editorialType(0.5): pageTitle=49, pageTitleLs=-0.046
const RED = '#E4544A';

const SORTS = [
  { id: 'mcap',  labelKey: 'mkt_sort_mcap' },
  { id: 'chg24', labelKey: 'mkt_col_24h' },
  { id: 'chg7d', labelKey: 'mkt_col_7d' },
  { id: 'price', labelKey: 'price' },
];

const TNUM = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };

function Sparkline({ data, color, width = 62, height = 26 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function StarToggle({ active, color, onToggle, size = 17, t }) {
  const favLabel = active ? t('mkt_fav_remove') : t('mkt_fav_add');
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title={favLabel}
      aria-label={favLabel}
      aria-pressed={active}
      style={{
        background: 'transparent', border: 'none', padding: 4,
        cursor: 'pointer', justifySelf: 'center',
        color: active ? color.activeColor : color.muteColor,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24"
           fill={active ? 'currentColor' : 'none'}
           stroke="currentColor" strokeWidth={active ? 1.5 : 2}
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
}

// ── Desktop Pill nach source/red-ui.jsx Pill (radius 3, 7px 13px) ──────────
function Pill({ T, active, onClick, children, icon, count }) {
  const { hover, handlers, motion } = usePillMotion();
  // Calm: aktiver Favoriten-Filter = ruhiger Ton-Button (ink-Fill), kein rotes
  // Füllen. Höhe + Form bündig zum Such-Feld.
  const bg = active ? T.text : (hover ? (T.itemHover || T.surfaceHi) : T.surfaceHi);
  const borderColor = active ? T.text : (hover ? T.text : T.line);
  const color = active ? T.bg : (hover ? T.text : T.sub);
  const hasCount = typeof count === 'number' && count > 0;
  return (
    <button
      onClick={onClick}
      {...handlers}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: 42, padding: '0 16px', borderRadius: mr(16),
        background: bg,
        border: `1px solid ${borderColor}`,
        color,
        fontFamily: F.family, fontWeight: 600, fontSize: 13,
        cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.01em',
        WebkitTapHighlightColor: 'transparent',
        ...motion,
      }}
    >
      {icon}
      {children}
      {hasCount && (
        <span style={{
          ...TNUM,
          fontSize: 11.5, fontWeight: 700,
          color: active ? T.bg : T.mute, opacity: active ? 0.7 : 1,
          paddingLeft: 1,
        }}>{count}</span>
      )}
    </button>
  );
}

// Mobile-Pill (Favoriten + SORTS), gleiche Motion-Sprache wie Desktop-Pill.
function MobilePillBtn({ T, active, onClick, children, ariaPressed }) {
  const { hover, handlers, motion } = usePillMotion();
  const bg = active ? T.text : (hover ? (T.itemHover || T.surfaceHi) : T.surface);
  const borderColor = active ? T.text : (hover ? T.text : T.line);
  const color = active ? T.bg : (hover ? T.text : T.sub);
  return (
    <button
      onClick={onClick}
      aria-pressed={ariaPressed}
      {...handlers}
      style={{
        padding: '7px 12px', borderRadius: mr(16),
        background: bg, border: `1px solid ${borderColor}`,
        color, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: F.family,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        WebkitTapHighlightColor: 'transparent',
        ...motion,
      }}
    >
      {children}
    </button>
  );
}

// ── Desktop MarketRow — Handoff grid 32/1.7/1/0.9/0.9/1.1/64/40 ────────────
function DesktopMarketRow({ T, coin, md, rank, isFav, onToggleFav, onSelect, last }) {
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const [hover, setHover] = useState(false);
  const { pressed, pressHandlers } = usePress({});
  const price = md?.price ?? 0;
  const chg24 = md?.chg ?? 0;
  const chg7d = md?.chg7d ?? chg24;
  const spark = md?.spark || [];
  const mcap = md?.market_cap || md?.mcap || 0;
  const pos7 = chg7d >= 0;
  const fmtBigCap = (n) => {
    if (!n) return '–';
    const c = convert(n);
    if (c >= 1e12) return `${symbol}${(c / 1e12).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('mkt_unit_trillion')}`;
    if (c >= 1e9)  return `${symbol}${(c / 1e9).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('mkt_unit_billion')}`;
    if (c >= 1e6)  return `${symbol}${(c / 1e6).toLocaleString(locale, { maximumFractionDigits: 0 })} ${t('mkt_unit_million')}`;
    return `${fmt(c, locale)} ${symbol}`;
  };

  return (
    <div
      className="mo-row"
      onClick={() => onSelect(coin)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...pressHandlers}
      style={{
        display: 'grid',
        gridTemplateColumns: '32px 1.7fr 1fr 0.9fr 0.9fr 1.1fr 64px 40px',
        alignItems: 'center', gap: 14,
        padding: '15px 6px',
        borderBottom: last ? 'none' : `1px solid ${T.lineSoft || T.line}`,
        cursor: 'pointer',
        background: pressed ? (T.surfaceHigh || T.itemHover) : (hover ? T.itemHover : 'transparent'),
        transition: `background ${DUR.fast}ms ease, color ${DUR.fast}ms`,
      }}
    >
      <span style={{ ...MONO_FEAT, fontSize: 11, fontWeight: 700, color: T.mute }}>
        {String(rank).padStart(2, '0')}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
          <CoinImage sym={coin.sym} marketData={{ [coin.id]: md }} size={34} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: F.family, fontWeight: 700, fontSize: 14,
            letterSpacing: '-0.02em', color: T.text,
          }}>{coin.sym}</div>
          <div style={{
            fontSize: 11.5, color: T.sub,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{coin.name}</div>
        </div>
      </div>

      <div style={{
        textAlign: 'right', ...MONO_FEAT,
        fontSize: 13.5, fontWeight: 700, color: T.text,
      }}>{price > 0 ? `${fmt(convert(price), locale)} ${symbol}` : '–'}</div>

      <div style={{ textAlign: 'right' }}>
        <span style={{
          ...MONO_FEAT, fontSize: 12.5, fontWeight: 700,
          color: chg24 >= 0 ? (T.gain || T.green) : (T.loss || T.red),
        }}>{chg24 >= 0 ? '+' : '−'}{Math.abs(chg24).toFixed(2)}%</span>
      </div>

      <div style={{ textAlign: 'right' }}>
        <span style={{
          ...MONO_FEAT, fontSize: 12.5, fontWeight: 700,
          color: chg7d >= 0 ? (T.gain || T.green) : (T.loss || T.red),
        }}>{chg7d >= 0 ? '+' : '−'}{Math.abs(chg7d).toFixed(2)}%</span>
      </div>

      <div style={{ textAlign: 'right', ...MONO_FEAT, fontSize: 12.5, color: T.sub }}>
        {fmtBigCap(mcap)}
      </div>

      <div style={{ justifySelf: 'center' }}>
        <Sparkline data={spark} color={pos7 ? (T.gain || T.green) : (T.loss || T.red)} width={68} height={28} />
      </div>

      <StarToggle
        active={isFav}
        color={{ activeColor: RED, muteColor: T.mute }}
        onToggle={() => onToggleFav(coin.sym)}
        t={t}
      />
    </div>
  );
}

// ── Mobile MarketRow — bestehende kompakte Variante ───────────────────────
function MobileMarketRow({ rank, coin, md, onSelect, isFav, onToggleFav, isLast, T }) {
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const price = md?.price ?? 0;
  const chg = md?.chg ?? 0;
  const spark = md?.spark;
  const pos = chg >= 0;
  const deltaColor = !isFinite(chg) ? T.mute : (pos ? (T.gain || T.green) : (T.loss || T.red));
  return (
    <div
      className="mo-row"
      onClick={() => onSelect(coin)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 18px',
        borderBottom: isLast ? 'none' : `1px solid ${T.line}`,
        cursor: 'pointer', color: T.text,
      }}
    >
      <div style={{ width: 22, flexShrink: 0, fontFamily: F.mono, fontSize: 10.5, fontWeight: 600, color: T.mute, letterSpacing: '0.02em', ...TNUM }}>
        {String(rank).padStart(2, '0')}
      </div>
      <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
        <CoinImage sym={coin.sym} marketData={{ [coin.id]: md }} size={32} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: F.display, fontWeight: 600, fontSize: 14, letterSpacing: '-0.02em', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coin.sym}</span>
        <span style={{ fontSize: 11, color: T.sub, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coin.name}</span>
      </div>
      <div style={{ flexShrink: 0, opacity: spark ? 0.9 : 0 }}>
        <Sparkline data={spark} color={deltaColor} width={52} height={22} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 72, flexShrink: 0 }}>
        <span style={{ fontFamily: F.display, fontWeight: 600, fontSize: 13.5, letterSpacing: '-0.02em', whiteSpace: 'nowrap', color: T.text, ...TNUM }}>
          {price > 0 ? `${fmt(convert(price), locale)} ${symbol}` : '–'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: deltaColor, whiteSpace: 'nowrap', ...TNUM }}>
          {isFinite(chg) ? `${pos ? '+' : '−'}${Math.abs(chg).toFixed(2)}%` : '–'}
        </span>
      </div>
      <StarToggle
        active={isFav}
        color={{ activeColor: T.red, muteColor: T.mute }}
        onToggle={() => onToggleFav(coin.sym)}
        size={18}
        t={t}
      />
    </div>
  );
}

export default function MarketsScreen({ marketData, onSelectAsset }) {
  const { T } = useTheme();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('mcap');
  const [favOnly, setFavOnly] = useState(false);
  const [liveData, setLiveData] = useState(marketData || {});
  const { isFavorite, toggleFavorite, favorites } = useFavorites();

  useEffect(() => {
    let alive = true;
    const ids = COIN_LIST.map(c => c.id);
    fetchMarketData(ids).then(data => {
      if (alive) setLiveData(prev => ({ ...prev, ...data }));
    });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = COIN_LIST;
    if (favOnly) list = list.filter(c => favorites.includes(c.sym));
    if (q) list = list.filter(c => c.sym.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    list = [...list];
    list.sort((a, b) => {
      const ma = liveData[a.id] || {};
      const mb = liveData[b.id] || {};
      if (sort === 'price') return (mb.price || 0) - (ma.price || 0);
      if (sort === 'chg24') return (mb.chg || 0) - (ma.chg || 0);
      if (sort === 'chg7d') return ((mb.chg7d ?? mb.chg) || 0) - ((ma.chg7d ?? ma.chg) || 0);
      return (mb.market_cap || mb.mcap || 0) - (ma.market_cap || ma.mcap || 0);
    });
    return list;
  }, [search, sort, liveData, favOnly, favorites]);

  // ── DESKTOP nach Handoff red-screens-markets.jsx ────────────────────────
  if (isWide) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: T.text, fontFamily: F.family }}>

        {/* Titel steht im App-Header (DesktopShell h1) — hier nur die Index-Tiles,
            als volle Breite über die ganze Zeile. */}
        <div style={{ borderBottom: `1px solid ${T.line}`, paddingBottom: 18 }}>
          <MarketIndices />
        </div>

        {/* Top-Mover — sentence-case Headline statt Caps-Kicker. */}
        <div>
          <div style={{
            fontFamily: F.display, fontSize: 18, fontWeight: 700,
            letterSpacing: '-0.02em', color: T.text, marginBottom: 14,
          }}>{t('top_mover')}</div>
          <TopMoverStrip variant="scroll" hideHeader marketData={liveData} onSelectAsset={onSelectAsset} />
        </div>

        {/* Markttabelle — flach (keine umrahmende Box), Haarlinien-Zeilen. */}
        <div>
          {/* Toolbar: Suche (soft) + Favoriten-Filter (Ton) + Sort-Segmented */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '0 0 6px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: T.surfaceHi, border: `1px solid ${T.line}`,
              borderRadius: mr(16), padding: '0 14px', height: 42,
              flex: '1 1 220px', maxWidth: 360,
            }}>
              <Icon d={Icons.search} size={16} stroke={T.sub} sw={1.8} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('search_markets')}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: T.text, fontSize: 13, fontFamily: F.family,
                }}
              />
            </div>
            <Pill T={T} active={favOnly} onClick={() => setFavOnly(v => !v)}
              count={favorites.length}
              icon={
                <svg width="14" height="14" viewBox="0 0 24 24"
                  fill={favOnly ? 'currentColor' : 'none'}
                  stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              }
            >
              {t('mkt_favorites')}
            </Pill>
            {/* Rechts: Sortier-Menü (Währungs-Umschalter entfernt — Währung steht
                im Profil / QuickSettings). */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Segmented
                value={sort}
                onChange={setSort}
                options={SORTS.map(s => ({ value: s.id, label: t(s.labelKey) }))}
              />
            </div>
          </div>

          {/* Sticky Tabellen-Header — sentence-case (kein Caps), flacher bg */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '32px 1.7fr 1fr 0.9fr 0.9fr 1.1fr 64px 40px',
            gap: 14,
            padding: '14px 6px 10px',
            fontFamily: F.family, fontSize: 11.5, fontWeight: 600,
            letterSpacing: '-0.005em',
            color: T.sub,
            borderBottom: `1px solid ${T.line}`,
            position: 'sticky', top: 0, background: T.bg, zIndex: 2,
          }}>
            <span>#</span>
            <span>{t('mkt_col_asset')}</span>
            <SortHeader id="price" active={sort === 'price'} onClick={setSort} accent={T.text} mute={T.mute}>{t('price')}</SortHeader>
            <SortHeader id="chg24" active={sort === 'chg24'} onClick={setSort} accent={T.text} mute={T.mute}>{t('mkt_col_24h')}</SortHeader>
            <SortHeader id="chg7d" active={sort === 'chg7d'} onClick={setSort} accent={T.text} mute={T.mute}>{t('mkt_col_7d')}</SortHeader>
            <SortHeader id="mcap" active={sort === 'mcap'} onClick={setSort} accent={T.text} mute={T.mute}>{t('mkt_col_mcap')}</SortHeader>
            <span style={{ textAlign: 'center' }}>{t('mkt_col_7d')}</span>
            <span />
          </div>

          {/* Rows */}
          <div>
            {filtered.slice(0, 100).map((coin, i, arr) => (
              <DesktopMarketRow
                key={coin.sym}
                T={T}
                coin={coin}
                md={liveData[coin.id]}
                rank={i + 1}
                isFav={isFavorite(coin.sym)}
                onToggleFav={toggleFavorite}
                onSelect={c => { const m = liveData[c.id] || {}; onSelectAsset({ sym: c.sym, coinId: c.id, name: c.name, price: m.price, chg: m.chg, image: m.image }); }}
                last={i === arr.length - 1 || i === 99}
              />
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '32px 18px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
                {favOnly ? t('mkt_empty_favorites') : t('mkt_empty_coins')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── MOBILE — bestehender Layout-Pfad ────────────────────────────────────
  return (
    <div style={{ paddingBottom: 100, color: T.text }}>
      {/* Titelzeile + Live-Ticker entfernt (Calm: ruhiger). */}

      {/* Markt-Indizes — eigene volle Reihe, drei gleichbreite Spalten. */}
      <div style={{ padding: '14px 18px 14px' }}>
        <MarketIndices variant="strip" />
      </div>

      <div style={{ padding: '0 18px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderRadius: mr(16), background: T.surfaceHi, border: `1px solid ${T.line}`, height: 42 }}>
          <Icon d={Icons.search} size={16} stroke={T.sub} sw={1.8} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('search_markets')}
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: T.text, fontSize: 13, fontFamily: F.family }}
          />
        </div>
      </div>

      <div className="hide-scrollbar" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 18px 16px' }}>
        <MobilePillBtn T={T} active={favOnly} ariaPressed={favOnly} onClick={() => setFavOnly(v => !v)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill={favOnly ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          {t('mkt_favorites')} {favorites.length > 0 && <span style={{ opacity: 0.85, ...TNUM }}>· {favorites.length}</span>}
        </MobilePillBtn>
        <div style={{ marginLeft: 'auto' }} />
        <Segmented
          value={sort}
          onChange={setSort}
          options={SORTS.map(s => ({ value: s.id, label: t(s.labelKey) }))}
        />
      </div>

      <div style={{ borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        {filtered.slice(0, 100).map((coin, i, arr) => (
          <MobileMarketRow
            key={coin.sym}
            rank={i + 1}
            coin={coin}
            md={liveData[coin.id]}
            onSelect={c => { const m = liveData[c.id] || {}; onSelectAsset({ sym: c.sym, coinId: c.id, name: c.name, price: m.price, chg: m.chg, image: m.image }); }}
            isFav={isFavorite(coin.sym)}
            onToggleFav={toggleFavorite}
            isLast={i === arr.length - 1 || i === 99}
            T={T}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: '32px 18px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
            {favOnly ? t('mkt_empty_favorites') : t('mkt_empty_coins')}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SortHeader — Klickbarer Spaltenkopf, ▾ wenn aktiv ─────────────────────
function SortHeader({ id, active, onClick, children, accent, mute }) {
  return (
    <button
      onClick={() => onClick(id)}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        fontFamily: F.family, fontSize: 11.5, fontWeight: active ? 700 : 600,
        letterSpacing: '-0.005em',
        color: active ? accent : mute,
        textAlign: 'right',
        justifySelf: 'end',
        display: 'inline-flex', alignItems: 'center', gap: 3,
      }}
    >
      {children}{active && <span style={{ fontSize: 9 }}>▾</span>}
    </button>
  );
}
